/* cspell:ignore cidr nics */
import { type ComponentProps, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Card, Checkbox, Descriptions, Empty, Form, Skeleton, Space, Typography } from '@arco-design/web-react';
import useSWR from 'swr';

import { GROUPS, PREFIX } from '@/service/apiConf';
import { get } from '@/service/request';
import {
  NIC_CONFIGURATION_FIELD,
  type NicConfigurationItem,
  type NicConfigurationProps,
  type NicConfigurationValue,
  type NicResourceConfiguration,
  type NicResourceQuantity,
} from '@/types/nic-configuration';

import styles from './index.module.less';
import {
  createNicConfigurationItem,
  formatNicHardwareSummary,
  getNicConfigurationCidrBlocks,
  getNicConfigurationKey,
  getNicDeviceType,
  isNicQuantityAvailable,
  isNicVendorDeviceMatch,
  setNicConfigurationCidrBlocks,
} from './utils';

export type {
  NicConfigurationItem,
  NicConfigurationProps,
  NicConfigurationValue,
  NicResource,
  NicResourceConfiguration,
  NicResourceQuantity,
  NicVendorDeviceSelection,
} from '@/types/nic-configuration';
export { NIC_CONFIGURATION_FIELD } from '@/types/nic-configuration';
export {
  buildNicSummaryLines,
  buildNicVendorDeviceSummary,
  formatNicHardwareSummary,
  formatNicSummaryValue,
  getNicDeviceType,
  isNicConfigurationComplete,
} from './utils';

/** 固定字段网卡表单项属性；允许使用方补充表单标签和校验规则。 */
export interface NicConfigurationFormItemProps {
  /** 表单字段路径，默认 `network.nics` */
  field?: string;
  label?: ComponentProps<typeof Form.Item>['label'];
  rules?: ComponentProps<typeof Form.Item>['rules'];
  /** 卡片内的厂商设备选择区；传入后替换默认连接信息，并按选中厂商设备筛选网段。 */
  renderVendorDeviceSelector?: NicConfigurationProps['renderVendorDeviceSelector'];
  /** 返回指定网卡类型当前选中的厂商设备。 */
  getSelectedVendorDevice?: NicConfigurationProps['getSelectedVendorDevice'];
}

/** 网卡配置预览属性。 */
export interface NicConfigurationPreviewProps {
  value?: NicConfigurationValue;
  /** 是否隐藏底层资源名称，并将同一网卡类型的 IPv4 网段合并展示。 */
  aggregateSubnetList?: boolean;
  /** 隐藏 Card 标题（外层自行渲染 section 标题） */
  hideTitle?: boolean;
}

const EMPTY_NIC_CONFIGURATIONS: NicResourceConfiguration[] = [];
const EMPTY_NIC_VALUE: NicConfigurationValue = [];
const NIC_RESOURCE_CONFIGURATIONS_KEY = 'nic-resource-configurations-container-instance';

function getNicResourceConfigurations() {
  const url = `${PREFIX.vapis_v1_dc_ws(GROUPS.clusterScheduler)}/resource-configurations`;
  return get<NicResourceConfiguration[]>(url, {
    workloadType: 'ContainerInstance',
    resourceType: 'NIC',
    availability: true,
  });
}

function getFallbackConfiguration(nic: NicConfigurationItem): NicResourceConfiguration {
  return {
    type: 'NIC',
    deviceType: nic.deviceType,
    vendor: nic.vendor,
    vendorID: nic.vendorID,
    device: nic.device,
    deviceID: nic.deviceID,
    resourceType: nic.resourceType,
    nic: { linkSpeed: nic.linkSpeed, linkType: nic.linkType },
    quantities: Object.entries(nic.resource).map(([resourceName, resource]) => ({
      resourceName,
      networkAttachmentDefinition: resource.networkAttachmentDefine,
      nic: { cidrBlocks: resource.cidrBlocks },
    })),
  };
}

function isIpv4Cidr(cidr: string) {
  return !cidr.includes(':');
}

/** 固定查询可用 ContainerInstance 网卡资源，并输出 network.nics 约定结构。 */
export function NicConfiguration({
  value = EMPTY_NIC_VALUE,
  onChange,
  renderVendorDeviceSelector,
  getSelectedVendorDevice,
  readOnly = false,
}: NicConfigurationProps) {
  const { t } = useTranslation();
  const { data, isLoading } = useSWR(NIC_RESOURCE_CONFIGURATIONS_KEY, getNicResourceConfigurations, { revalidateOnFocus: false });
  const configurations = data || EMPTY_NIC_CONFIGURATIONS;
  const configurationsByDeviceType = useMemo(() => {
    const result = new Map<string, NicResourceConfiguration[]>();
    const append = (configuration: NicResourceConfiguration) => {
      const deviceType = getNicDeviceType(configuration);
      if (!deviceType) return;
      const group = result.get(deviceType) || [];
      if (!group.some((item) => getNicConfigurationKey(item) === getNicConfigurationKey(configuration))) {
        result.set(deviceType, [...group, configuration]);
      }
    };
    configurations.forEach((configuration) => {
      append(configuration);
    });
    value.forEach((nic) => {
      append(getFallbackConfiguration(nic));
    });
    return result;
  }, [configurations, value]);

  const handleCidrChange = (configuration: NicResourceConfiguration, quantity: NicResourceQuantity, cidrBlocks: string[]) => {
    if (readOnly) return;
    const configurationKey = getNicConfigurationKey(configuration);
    const current = value.find((nic) => getNicConfigurationKey(nic) === configurationKey) || createNicConfigurationItem(configuration);
    const nextNic = setNicConfigurationCidrBlocks(current, quantity, cidrBlocks);
    const nextByKey = new Map(value.map((nic) => [getNicConfigurationKey(nic), nic]));
    if (Object.keys(nextNic.resource).length) nextByKey.set(configurationKey, nextNic);
    else nextByKey.delete(configurationKey);
    const configurationKeys = [...configurationsByDeviceType.values()].flatMap((items) => items.map(getNicConfigurationKey));
    onChange?.(configurationKeys.flatMap((key) => (nextByKey.has(key) ? [nextByKey.get(key)!] : [])));
  };

  if (isLoading) return <Skeleton loading animation text={{ rows: 3 }} />;
  if (!configurationsByDeviceType.size) return <Empty description={t('data-center:no.available.resources')} />;

  return (
    <Space direction="vertical" size="medium" style={{ width: '100%' }}>
      {[...configurationsByDeviceType.entries()].map(([deviceType, deviceTypeConfigurations]) => {
        const selection = getSelectedVendorDevice?.(deviceType);
        const visibleConfigurations = getSelectedVendorDevice
          ? selection
            ? deviceTypeConfigurations.filter((configuration) => isNicVendorDeviceMatch(configuration, selection))
            : deviceTypeConfigurations
          : deviceTypeConfigurations;
        const renderSubnetList = (configuration: NicResourceConfiguration) => {
          const nic = value.find((item) => getNicConfigurationKey(item) === getNicConfigurationKey(configuration));
          return (configuration.quantities || []).map((quantity) => {
            const available = isNicQuantityAvailable(quantity);
            const selectedCidrs = (nic?.resource[quantity.resourceName]?.cidrBlocks || []).filter(isIpv4Cidr);
            const cidrBlocks = [
              ...new Set([...(quantity.nic?.cidrBlocks || []), ...selectedCidrs].filter((cidr) => cidr && isIpv4Cidr(cidr))),
            ];
            if (!cidrBlocks.length) return null;
            return (
              <Checkbox.Group
                key={`${getNicConfigurationKey(configuration)}-${quantity.resourceName}`}
                value={selectedCidrs}
                onChange={(nextCidrs) => handleCidrChange(configuration, quantity, nextCidrs)}
                className={styles.subnetGroup}
              >
                {cidrBlocks.map((cidr) => {
                  const selected = selectedCidrs.includes(cidr);
                  return (
                    <Checkbox
                      key={cidr}
                      value={cidr}
                      disabled={!available || readOnly}
                      className={`${styles.subnetOption} ${selected ? styles.subnetOptionSelected : ''} ${
                        available ? '' : styles.subnetOptionDisabled
                      }`}
                    >
                      <span className={styles.subnetContent}>
                        <span className={styles.cidr}>{cidr}</span>
                        <span className={`${styles.availability} ${available ? styles.available : styles.unavailable}`}>
                          <span className={styles.statusDot} />
                          {t(available ? 'cluster:available' : 'cluster:unavailable')}
                        </span>
                      </span>
                    </Checkbox>
                  );
                })}
              </Checkbox.Group>
            );
          });
        };
        return (
          <Card key={deviceType} bordered className={styles.nicCard}>
            <Typography.Title heading={5} className={styles.cardTitle}>
              {deviceType} {t('common:type')}
            </Typography.Title>
            {renderVendorDeviceSelector ? (
              <>
                <div className={styles.vendorDeviceSlot}>{renderVendorDeviceSelector(deviceType)}</div>
                {selection && !visibleConfigurations.length ? <Empty description={t('data-center:no.available.resources')} /> : null}
                {visibleConfigurations.length ? (
                  <div className={`${styles.subnetList} ${styles.subnetListAfterVendor}`}>
                    {visibleConfigurations.flatMap(renderSubnetList)}
                  </div>
                ) : null}
              </>
            ) : (
              deviceTypeConfigurations.map((configuration) => {
                const vendorAndDevice = formatNicHardwareSummary(configuration);
                const hardwareDetails = [configuration.nic?.linkSpeed, configuration.nic?.linkType].filter(Boolean);
                return (
                  <div key={getNicConfigurationKey(configuration)} className={styles.configurationBlock}>
                    <div className={styles.hardwareSummary}>
                      <span className={styles.deviceSummary}>{vendorAndDevice}</span>
                      {hardwareDetails.map((detail) => (
                        <span key={detail} className={styles.hardwareDetail}>
                          {detail}
                        </span>
                      ))}
                    </div>
                    <div className={styles.subnetList}>{renderSubnetList(configuration)}</div>
                  </div>
                );
              })
            )}
          </Card>
        );
      })}
    </Space>
  );
}

/** 使用固定 network.nics 字段注册网卡配置组件。 */
export function NicConfigurationFormItem({
  field = NIC_CONFIGURATION_FIELD,
  label,
  rules,
  renderVendorDeviceSelector,
  getSelectedVendorDevice,
}: NicConfigurationFormItemProps) {
  return (
    <Form.Item field={field} label={label} rules={rules}>
      <NicConfiguration renderVendorDeviceSelector={renderVendorDeviceSelector} getSelectedVendorDevice={getSelectedVendorDevice} />
    </Form.Item>
  );
}

/** 只读展示组件输出的网卡配置。 */
export function NicConfigurationPreview({ value, aggregateSubnetList = false, hideTitle = false }: NicConfigurationPreviewProps) {
  const { t } = useTranslation();
  if (!value?.length) return null;

  const content = (
    <Space direction="vertical" size="medium" style={{ width: '100%' }}>
      {value.map((nic) => (
        <Descriptions
          key={getNicConfigurationKey(nic)}
          title={`${getNicDeviceType(nic)} ${t('common:type')}`}
          column={2}
          border
          data={[
            { label: t('product:resource.vendor'), value: nic.vendor || '-' },
            { label: t('product:resource.device'), value: nic.device || '-' },
            {
              label: t('data-center:training.cluster.nic.connection.info'),
              value: [nic.linkType, nic.linkSpeed].filter(Boolean).join(' · ') || '-',
            },
            ...(aggregateSubnetList
              ? [
                  {
                    label: t('data-center:network.segment.list'),
                    value: getNicConfigurationCidrBlocks(nic).join(', ') || '-',
                  },
                ]
              : Object.entries(nic.resource).map(([resourceName, resource]) => ({
                  label: resourceName,
                  value: resource.cidrBlocks.filter(isIpv4Cidr).join(', '),
                }))),
          ]}
        />
      ))}
    </Space>
  );

  if (hideTitle) {
    return <div style={{ marginBottom: 12 }}>{content}</div>;
  }

  return (
    <Card title={t('data-center:network.card')} bordered={false} style={{ marginBottom: 12 }}>
      {content}
    </Card>
  );
}
